"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useLobby } from "@/providers/lobby-provider";
import { Flag, X, ChevronUp } from "lucide-react";
import "@/styles/lobby-nav.css";

interface LobbyNavIndicatorProps {
  className?: string;
}

export function LobbyNavIndicator({ className }: LobbyNavIndicatorProps) {
  const router = useRouter();
  const { activeLobby, lobbyPlayers } = useLobby();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!activeLobby) return null;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleGoToLobby = () => {
    router.push(`/local-links-v1/matchmaking/lobby/${activeLobby.id}`);
  };

  const handlePlayerClick = (userId: string) => {
    router.push(`/local-links-v1/users/${userId}/profile`);
  };

  // Show max 3 collapsed player tokens
  const collapsedPlayers = lobbyPlayers.slice(0, 3);
  const extraPlayerCount = lobbyPlayers.length - 3;

  return (
    <>
      {/* Expanded overlay panel */}
      {isExpanded && (
        <div className="lobby-expanded-overlay" onClick={handleToggle}>
          <div
            className="lobby-expanded-panel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="lobby-panel-header">
              <div className="lobby-panel-title">
                <Flag className="h-4 w-4" />
                <span>{activeLobby.title}</span>
              </div>
              <button className="lobby-panel-close" onClick={handleToggle}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Players grid */}
            <div className="lobby-panel-players">
              {lobbyPlayers.length === 0 ? (
                <p className="lobby-panel-empty">No other players yet</p>
              ) : (
                lobbyPlayers.map((player, index) => (
                  <button
                    key={player.userId}
                    className="lobby-panel-player"
                    onClick={() => handlePlayerClick(player.userId)}
                    style={{ "--player-index": index } as React.CSSProperties}
                  >
                    <div className="lobby-panel-player-avatar">
                      {player.photoURL ? (
                        <img src={player.photoURL} alt={player.displayName} />
                      ) : (
                        <div className="lobby-panel-player-initials">
                          {player.displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="lobby-panel-player-name">
                      {player.displayName.split(" ")[0]}
                    </span>
                  </button>
                ))
              )}
            </div>

            {/* Go to lobby button */}
            <button className="lobby-panel-go-button" onClick={handleGoToLobby}>
              Go to Lobby
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main toggle button */}
      <div
        className={`lobby-nav-indicator ${isExpanded ? "expanded" : ""} ${
          className || ""
        }`}
        onClick={handleToggle}
      >
        {/* Collapsed player tokens (shown when collapsed and has players) */}
        {!isExpanded && collapsedPlayers.length > 0 && (
          <div className="lobby-player-tokens-collapsed">
            {collapsedPlayers.map((player, index) => (
              <div
                key={player.userId}
                className="lobby-player-token-small"
                style={{ "--token-index": index } as React.CSSProperties}
              >
                {player.photoURL ? (
                  <img src={player.photoURL} alt={player.displayName} />
                ) : (
                  <div className="lobby-player-token-placeholder-small">
                    {player.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ))}
            {extraPlayerCount > 0 && (
              <div className="lobby-player-token-extra">
                +{extraPlayerCount}
              </div>
            )}
          </div>
        )}

        {/* Main glowing indicator */}
        <div className={`lobby-glow-ring ${isExpanded ? "expanded" : ""}`}>
          <div className="lobby-icon-container">
            <Flag />
          </div>
        </div>

        {/* Label */}
        <span className="lobby-nav-label">
          {isExpanded ? "Close" : "Lobby"}
        </span>
      </div>
    </>
  );
}
